import React, { useState, useEffect } from 'react';
import { Box, Paper, Grid, TextField, Button, Alert, Skeleton, Switch } from '@mui/material';
import api from '../config/api';

const ff = "'Poppins', sans-serif";

// Config fields grouped into sections for clarity. Add a new entry here (and the
// matching key in the backend appConfig util) when you need another value.
const SECTIONS = [
  {
    title: 'Support contacts',
    subtitle: 'Used by the app\'s Help & Support screen. A blank field shows as "under maintenance" in the app.',
    fields: [
      { key: 'supportWhatsapp', label: 'WhatsApp number', help: 'Powers the "Chat with Support" button (opens WhatsApp).', placeholder: '+1 (778) 772-3666' },
      { key: 'supportEmail', label: 'Support email', help: 'Powers the "Email Support" button.', placeholder: 'support@oktreat.com' },
      { key: 'supportPhone', label: 'Support phone', help: 'Powers the "Call OkTreat Support" button.', placeholder: '+1 (778) 772-3666' },
    ],
  },
  {
    title: 'Store links',
    subtitle: 'Used by the public tag page and the referral share message.',
    fields: [
      { key: 'playStoreUrl', label: 'Google Play URL', help: 'Android store listing.', placeholder: 'https://play.google.com/store/apps/details?id=com.oktreat.app' },
      { key: 'appStoreUrl', label: 'App Store URL', help: 'iOS store listing.', placeholder: 'https://apps.apple.com/us/app/...' },
    ],
  },
  {
    title: 'Other links',
    subtitle: '',
    fields: [
      { key: 'amazonTagsUrl', label: 'Amazon "Buy more tags" URL', help: 'Where "Buy More Tags" sends users. Blank = shown as under development.', placeholder: 'https://www.amazon.com/...' },
    ],
  },
  {
    title: 'Missing pet alerts',
    subtitle: 'When an owner reports a pet missing, every user with a stored location within this radius of the last-seen spot gets a "missing pet near you" alert.',
    fields: [
      { key: 'missingPetAlertRadiusKm', label: 'Alert radius (km)', help: 'Default 25. Allowed range 1–500.', placeholder: '25', type: 'number' },
    ],
  },
];

const Field = ({ label, children, help }) => (
  <Box>
    <Box sx={{ fontFamily: ff, fontSize: 12, fontWeight: 600, color: '#4A5568', mb: 0.5 }}>{label}</Box>
    {children}
    {help && <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0', mt: 0.5 }}>{help}</Box>}
  </Box>
);

// A titled config section card.
const SectionCard = ({ title, subtitle, children }) => (
  <Paper sx={{ p: 3, borderRadius: '16px', mb: 2 }}>
    <Box sx={{ fontFamily: ff, fontSize: 15, fontWeight: 600, color: '#1A202C' }}>{title}</Box>
    {subtitle && <Box sx={{ fontFamily: ff, fontSize: 12, color: '#A0AEC0', mt: 0.5, mb: 2, maxWidth: 560 }}>{subtitle}</Box>}
    <Box sx={{ mt: subtitle ? 0 : 2 }}>{children}</Box>
  </Paper>
);

export default function AppConfig() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/app-config');
        setS((res.data.data || res.data).settings);
      } catch (e) { setError('Failed to load app config.'); }
      finally { setLoading(false); }
    })();
  }, []);

  const set = (patch) => { setS((prev) => ({ ...prev, ...patch })); setSaved(false); };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const res = await api.put('/admin/app-config', s);
      setS((res.data.data || res.data).settings);
      setSaved(true);
    } catch (e) { setError(e.response?.data?.message || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  if (loading || !s) {
    return <Box sx={{ p: 1 }}><Skeleton variant="rounded" height={360} /></Box>;
  }

  return (
    <Box sx={{ fontFamily: ff, maxWidth: 760 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>Settings saved.</Alert>}

      <Alert severity="info" sx={{ mb: 2, fontFamily: ff }}>
        These values are fetched by the app on launch, so changes take effect on the next app open — no new build required.
      </Alert>

      {/* Maintenance mode (mobile app only). When ON, the app blocks every user
          at the splash with the title/message below. Fully admin-controlled. */}
      <Paper sx={{ p: 3, borderRadius: '16px', mb: 2, border: s.maintenance?.enabled ? '1px solid #FEB2B2' : 'none', bgcolor: s.maintenance?.enabled ? '#FFF5F5' : '#FFFFFF' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box>
            <Box sx={{ fontFamily: ff, fontSize: 14, fontWeight: 600, color: '#1A202C' }}>
              Maintenance mode <Box component="span" sx={{ fontSize: 12, color: '#A0AEC0', fontWeight: 500 }}>(mobile app)</Box>
            </Box>
            <Box sx={{ fontFamily: ff, fontSize: 12, color: '#A0AEC0', mt: 0.5, maxWidth: 520 }}>
              When ON, the mobile app blocks all users at the splash screen with the title and message below. It takes effect on each app launch (or when a blocked user taps Retry). No app rebuild needed.
            </Box>
          </Box>
          <Switch
            checked={!!s.maintenance?.enabled}
            onChange={(e) => set({ maintenance: { ...(s.maintenance || {}), enabled: e.target.checked } })}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#E53E3E' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#E53E3E' },
            }}
          />
        </Box>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <Field label="Maintenance title" help="Short heading shown on the maintenance screen.">
              <TextField fullWidth size="small" value={s.maintenance?.title || ''}
                placeholder="We'll be right back"
                onChange={(e) => set({ maintenance: { ...(s.maintenance || {}), title: e.target.value } })} />
            </Field>
          </Grid>
          <Grid item xs={12}>
            <Field label="Maintenance message" help="Explain what's happening and when to check back.">
              <TextField fullWidth size="small" multiline minRows={2} value={s.maintenance?.message || ''}
                placeholder="OkTreat is undergoing scheduled maintenance. Please check back shortly."
                onChange={(e) => set({ maintenance: { ...(s.maintenance || {}), message: e.target.value } })} />
            </Field>
          </Grid>
        </Grid>
      </Paper>

      {SECTIONS.map((section) => (
        <SectionCard key={section.title} title={section.title} subtitle={section.subtitle}>
          <Grid container spacing={2.5}>
            {section.fields.map((f) => (
              <Grid item xs={12} key={f.key}>
                <Field label={f.label} help={f.help}>
                  <TextField fullWidth size="small" type={f.type || 'text'}
                    value={s[f.key] ?? ''}
                    placeholder={f.placeholder}
                    inputProps={f.type === 'number' ? { min: 1, max: 500 } : undefined}
                    onChange={(e) => set({ [f.key]: e.target.value })} />
                </Field>
              </Grid>
            ))}
          </Grid>
        </SectionCard>
      ))}

      <Button variant="contained" onClick={save} disabled={saving}
        sx={{ fontFamily: ff, textTransform: 'none', bgcolor: '#32A6D8', px: 4 }}>
        {saving ? 'Saving…' : 'Save settings'}
      </Button>
    </Box>
  );
}
